// components/Staffrecord/Editform/Qualification.jsx
import React, { useEffect } from 'react';
import '../../../styles/Qualification.css';

const Qualification = ({ formData = {}, onFormDataChange, isEditing = true }) => {
  const currentYear = new Date().getFullYear();
  const MAX_EXPERIENCE_ROWS = 3;

  // Initialize rows count
  useEffect(() => {
    if (!formData.experienceRows) {
      onFormDataChange(prev => ({ ...prev, experienceRows: 1 }));
    }
  }, []);

  // Handle input changes
  const handleInputChange = (field, value) => {
    onFormDataChange(prev => ({ ...prev, [field]: value }));
  };

  const handleExperienceChange = (index, fieldSuffix, value) => {
    const fieldName = `${fieldSuffix}${index}`;
    handleInputChange(fieldName, value);
  };

  // Input renderer
  const renderInput = (key, type = 'text', placeholder = '-', readOnly = false, extraProps = {}) => {
    let value = formData[key];

    if (key.includes("expCurrentlyWorking") && (value === "" || value === undefined || value === null)) {
      value = false;
    }

    if (!isEditing) {
      if (type === 'date' && value) {
        const d = new Date(value);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
      return value || '';
    }

    const inputValue = value ?? '';

    return (
      <input
        type={type}
        value={inputValue}
        onChange={(e) => {
          const val = type === 'number'
            ? (e.target.value === '' ? '' : Number(e.target.value))
            : e.target.value;

          handleInputChange(key, val);
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        {...extraProps}
      />
    );
  };

  const experienceRows = Number(formData.experienceRows || 1);
  const hasExperience = formData.experience && formData.experience !== "Fresher";

  const addExperienceRow = () => {
    const next = Math.min(experienceRows + 1, MAX_EXPERIENCE_ROWS);
    handleInputChange("experienceRows", next);
  };

  const removeExperienceRow = () => {
    const removeIndex = experienceRows;
    onFormDataChange(prev => {
      const copy = { ...prev, experienceRows: experienceRows - 1 };
      ["expCompanyName", "expStartDate", "expEndDate", "expRole", "expTechnologies", "expCurrentlyWorking"].forEach(prefix => {
        copy[`${prefix}${removeIndex}`] = null;
      });
      return copy;
    });
  };

  return (
    <div>

      {/* EDUCATION TABLE */}
      <div className="education-table">
        <table>
          <thead>
            <tr>
              <th>Education Level</th>
              <th>Degree</th>
              <th>Department</th>
              <th>Institution Name and Address</th>
              <th>Passed Out Year</th>
              <th>Percentage / CGPA</th>
            </tr>
          </thead>

          <tbody>

            {/* ==== 10th ==== */}
            <tr>
              <td>10th</td>
              <td>{renderInput("tenthDegree", "text", "-", true)}</td>
              <td>{renderInput("tenthDepartment", "text", "-", true)}</td>
              <td>{renderInput("tenthInstitution", "text", "Institution")}</td>
              <td>{renderInput("tenthPassout", "number", "YYYY")}</td>
              <td>{renderInput("tenthPercentage", "number", "%")}</td>
            </tr>

            {/* ==== 12th ==== */}
            <tr>
              <td>12th</td>
              <td>{renderInput("twelfthDegree", "text", "-", true)}</td>
              <td>{renderInput("twelfthDepartment", "text", "-", true)}</td>
              <td>{renderInput("twelfthInstitution", "text", "Institution")}</td>
              <td>{renderInput("twelfthPassout", "number", "YYYY")}</td>
              <td>{renderInput("twelfthPercentage", "number", "%")}</td>
            </tr>

            {/* ==== Diploma ==== */}
            <tr>
              <td>Diploma</td>
              <td>
                <select
                  value={formData.diplomaDegree || ""}
                  onChange={(e) => handleInputChange("diplomaDegree", e.target.value)}
                >
                  <option value="">Select Degree</option>
                  <option value="Diploma">Diploma</option>
                </select>
              </td>      
              <td>{renderInput("diplomaDepartment", "text", "Department")}</td>
              <td>{renderInput("diplomaInstitution", "text", "Institution")}</td>
              <td>{renderInput("diplomaPassout", "number", "YYYY")}</td>
              <td>{renderInput("diplomaPercentage", "number", "%")}</td>
            </tr>

            {/* ==== UG ==== */}
            <tr>
              <td>UG</td>

              {/* UG Degree */}
              <td>
                {isEditing ? (
                  (formData.ugDegreeIsOther || formData.ugDegreeDropdown === "Other") ? (
                    <>
                      <input
                        type="text"
                        value={formData.ugDegree || ""}
                        placeholder="Your Degree"
                        onChange={(e) => handleInputChange("ugDegree", e.target.value)}
                      />
                      <button
                        type="button"
                        className="switch-btn"
                        onClick={() => {
                          handleInputChange("ugDegreeIsOther", false);
                          handleInputChange("ugDegreeDropdown", "");
                        }}
                      >
                        Switch to Dropdown
                      </button>
                    </>
                  ) : (
                    <select
                      value={formData.ugDegree || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "Other") {
                          handleInputChange("ugDegreeIsOther", true);
                          handleInputChange("ugDegreeDropdown", "Other");
                          handleInputChange("ugDegree", "");
                        } else {
                          handleInputChange("ugDegreeIsOther", false);
                          handleInputChange("ugDegreeDropdown", v);
                          handleInputChange("ugDegree", v);
                        }
                      }}
                    >
                      <option value="">Select Degree</option>
                      <option value="B.Tech">B.Tech</option>
                      <option value="B.E">B.E</option>
                      <option value="B.Sc">B.Sc</option>
                      <option value="B.Com">B.Com</option>
                      <option value="B.A">B.A</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : formData.ugDegree}
              </td>

              {/* UG Department */}
              <td>
                {isEditing ? (
                  (formData.ugDepartmentIsOther) ? (
                    <>
                      <input
                        type="text"
                        value={formData.ugDepartment || ""}
                        placeholder="Your Department"
                        onChange={(e) => handleInputChange("ugDepartment", e.target.value)}
                      />
                      <button
                        type="button"
                        className="switch-btn"
                        onClick={() => {
                          handleInputChange("ugDepartmentIsOther", false);
                        }}
                      >
                        Switch to Dropdown
                      </button>
                    </>
                  ) : (
                    <select
                      value={formData.ugDepartment || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "Other") {
                          handleInputChange("ugDepartmentIsOther", true);
                          handleInputChange("ugDepartment", "");
                        } else {
                          handleInputChange("ugDepartmentIsOther", false);
                          handleInputChange("ugDepartment", v);
                        }
                      }}
                    >
                      <option value="">Select Department</option>
                      <option value="CSE">CSE</option>
                      <option value="IT">IT</option>
                      <option value="Mechanical">Mechanical</option>
                      <option value="ECE">ECE</option>
                      <option value="EEE">EEE</option>
                      <option value="Civil">Civil</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : formData.ugDepartment}
              </td>

              <td>{renderInput("ugInstitution", "text", "Institution")}</td>
              <td>{renderInput("ugPassout", "number", "YYYY")}</td>
              <td>{renderInput("ugPercentage", "number", "%")}</td>
            </tr>

            {/* ==== PG ==== */}
            <tr>
              <td>PG</td>

              {/* PG Degree */}
              <td>
                {isEditing ? (
                  (formData.pgDegreeIsOther) ? (
                    <>
                      <input
                        type="text"
                        value={formData.pgDegree || ""}
                        placeholder="Your Degree"
                        onChange={(e) => handleInputChange("pgDegree", e.target.value)}
                      />
                      <button
                        type="button"
                        className="switch-btn"
                        onClick={() => {
                          handleInputChange("pgDegreeIsOther", false);
                        }}
                      >
                        Switch to Dropdown
                      </button>
                    </>
                  ) : (
                    <select
                      value={formData.pgDegree || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "Other") {
                          handleInputChange("pgDegreeIsOther", true);
                          handleInputChange("pgDegree", "");
                        } else {
                          handleInputChange("pgDegreeIsOther", false);
                          handleInputChange("pgDegree", v);
                        }
                      }}
                    >
                      <option value="">Select Degree</option>
                      <option value="M.S">M.S</option>
                      <option value="M.Tech">M.Tech</option>
                      <option value="MBA">MBA</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : formData.pgDegree}
              </td>

              {/* PG Department */}
              <td>
                {isEditing ? (
                  (formData.pgDepartmentIsOther) ? (
                    <>
                      <input
                        type="text"
                        value={formData.pgDepartment || ""}
                        placeholder="Your Department"
                        onChange={(e) => handleInputChange("pgDepartment", e.target.value)}
                      />
                      <button
                        type="button"
                        className="switch-btn"
                        onClick={() => {
                          handleInputChange("pgDepartmentIsOther", false);
                        }}
                      >
                        Switch to Dropdown
                      </button>
                    </>
                  ) : (
                    <select
                      value={formData.pgDepartment || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "Other") {
                          handleInputChange("pgDepartmentIsOther", true);
                          handleInputChange("pgDepartment", "");
                        } else {
                          handleInputChange("pgDepartmentIsOther", false);
                          handleInputChange("pgDepartment", v);
                        }
                      }}
                    >
                      <option value="">Select Department</option>
                      <option value="CSE">CSE</option>
                      <option value="IT">IT</option>
                      <option value="Mechanical">Mechanical</option>
                      <option value="ECE">ECE</option>
                      <option value="EEE">EEE</option>
                      <option value="Civil">Civil</option>
                      <option value="Other">Other</option>
                    </select>
                  )
                ) : formData.pgDepartment}
              </td>

              <td>{renderInput("pgInstitution", "text", "Institution")}</td>
              <td>{renderInput("pgPassout", "number", "YYYY")}</td>
              <td>{renderInput("pgPercentage", "number", "%")}</td>
            </tr>

          </tbody>
        </table>
      </div>

      {/* ===================== EXPERIENCE SECTION ===================== */}
      <div style={{ marginTop: 30 }}>
        <label style={{ fontWeight: "bold", fontSize: 16 }}>Work Experience:</label>

        {isEditing && (
          <select
            value={formData.experience || ""}
            onChange={(e) => handleInputChange("experience", e.target.value)}
            style={{ marginLeft: 10, fontFamily: "Times New Roman" }}
          >
            <option value="">Select Experience</option>
            <option value="Fresher">Fresher</option>
            <option value="<1year">&lt; 1 year</option>
            <option value="1-2years">1 - 2 years</option>
            <option value="2-3years">2 - 3 years</option>
            <option value=">3years">&gt; 3 years</option>
          </select>
        )}

        {hasExperience && (
          <div>
            <table className="experience-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Role</th>
                  <th>Technologies</th>
                  {isEditing && <th>Action</th>}
                </tr>
              </thead>

              <tbody>
                {Array.from({ length: experienceRows }).map((_, idx) => {
                  const i = idx + 1;

                  return (
                    <tr key={i}>
                      <td>
                        <input
                          value={formData[`expCompanyName${i}`] || ""}
                          onChange={(e) => handleExperienceChange(i, "expCompanyName", e.target.value)}
                        />
                      </td>

                      <td>
                        <input
                          type="date"
                          value={formData[`expStartDate${i}`] || ""}
                          onChange={(e) => handleExperienceChange(i, "expStartDate", e.target.value)}
                        />
                      </td>

                      <td>
                        {formData[`expCurrentlyWorking${i}`] ? (
                          <input type="text" value="Present" disabled />
                        ) : (
                          <input
                            type="date"
                            value={formData[`expEndDate${i}`] || ""}
                            onChange={(e) => handleExperienceChange(i, "expEndDate", e.target.value)}
                          />
                        )}

                        <div>
                          <input
                            type="checkbox"
                            checked={formData[`expCurrentlyWorking${i}`] || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              handleExperienceChange(i, "expCurrentlyWorking", checked);
                              if (checked) handleExperienceChange(i, "expEndDate", null);
                            }}
                          />
                          <label>Currently Working</label>
                        </div>
                      </td>

                      <td>
                        <input
                          value={formData[`expRole${i}`] || ""}
                          onChange={(e) => handleExperienceChange(i, "expRole", e.target.value)}
                        />
                      </td>

                      <td>
                        <input
                          value={formData[`expTechnologies${i}`] || ""}
                          onChange={(e) => handleExperienceChange(i, "expTechnologies", e.target.value)}
                        />
                      </td>

                      {isEditing && i === experienceRows && experienceRows > 1 && (
                        <td>
                          <button onClick={removeExperienceRow}>Remove</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {isEditing && experienceRows < MAX_EXPERIENCE_ROWS && (
              <button type="button" onClick={addExperienceRow} className="add-row-btn">
                + Add Another Company
              </button>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default Qualification;
